<script setup lang="ts">
import { SelectOption } from "@/models/frontend-only/SelectOption";

const props = withDefaults(
  defineProps<{
    label?: string;
    options?: (SelectOption | string)[];
    modelValue?: string;
    name?: string;
    suffix?: string;
    id?: string;
    min?: string;
    max: string;
    step?: string;
    increaseLabel: string;
    decreaseLabel: string;
    hideLabel?: boolean;
  }>(),
  {
    label: "",
    modelValue: "",
    min: "0",
    step: "1",
    options: () => [],
    id: () => uniqueId("form-slider"),
    hideLabel: false,
  },
);

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
}>();

const data = computed({
  get() {
    return props.modelValue;
  },
  set(newValue) {
    emit("update:modelValue", newValue);
  },
});

function increase() {
  data.value = Math.min(
    parseFloat(props.max),
    parseFloat(data.value) + parseFloat(props.step),
  ).toString();
}
function decrease() {
  data.value = Math.max(
    parseFloat(props.min),
    parseFloat(data.value) - parseFloat(props.step),
  ).toString();
}

watch(
  () => data.value,
  (v) => {
    (window as any).dataLayer.push({
      font_size: v,
    });
  },
);
</script>

<template>
  <div class="form-group">
    <label
      :for="id"
      :class="{ 'sr-only': hideLabel }"
      class="label"
      ><span class="label-text"
        >{{ label }} - {{ data }}{{ suffix }}</span
      ></label
    >
    <div class="range-group">
      <button
        type="button"
        :aria-label="decreaseLabel"
        @click="decrease()"
      >
        <FAIcon class="fa-light fa-minus" />
      </button>
      <input
        :id="id"
        v-model="data"
        :name="name"
        type="range"
        :min="min"
        :max="max"
        :step="step"
        class="range range-primary"
      />
      <button
        type="button"
        :aria-label="increaseLabel"
        @click="increase()"
      >
        <FAIcon class="fa-light fa-plus" />
      </button>
    </div>
  </div>
</template>

<style scoped lang="postcss">
button {
  @apply flex h-8 w-8 items-center justify-center rounded-full transition;

  &:hover {
    @apply bg-light-700;
  }

  &:focus-visible {
    @apply bg-light-700 outline outline-primary;
  }
}

.range-group {
  @apply flex items-center gap-2;

  input {
    @apply flex-grow;
  }
}

.range-display {
  margin-right: 0.5em;
  padding: 0.25em 0.5em;
  border-radius: 0.5em;
  @apply border border-gray-100 bg-white;
}

button:focus-visible {
  outline: 2px solid var(--ccli-black);
}

input[type="range"] {
  padding: 10px 0px; /* Add padding to ensure thumb is fully visible */
  background-color: white;
}

input[type="range"]::-webkit-slider-thumb {
  width: 20px;
  height: 20px;
  background: #fff;
  border-radius: 50%;
  cursor: pointer;
}

input[type="range"]::-moz-range-thumb {
  width: 20px;
  height: 20px;
  background: #fff;
  border-radius: 50%;
  cursor: pointer;
}

input[type="range"]::-ms-thumb {
  width: 20px;
  height: 20px;
  background: #fff;
  border-radius: 50%;
  cursor: pointer;
}
</style>
